<template>
  <div class="operation-dashboard">
    <div class="operation-dashboard__heading">
      <h1>{{ dashboardTitle }}</h1>
      <div class="operation-dashboard__heading-right">
        <span>{{ updatedAtText }}</span>
        <button
          class="export-button"
          :disabled="!overview || exporting"
          @click="handleExport"
        >
          <el-icon>
            <Download />
          </el-icon>
          {{ exporting ? '导出中...' : '导出报表' }}
        </button>
      </div>
    </div>

    <section class="stat-grid">
      <article v-for="stat in statCards" :key="stat.label" class="stat-card">
        <div class="stat-card__top">
          <span>{{ stat.label }}</span>
          <span class="stat-card__icon" :class="`is-${stat.tone ?? 'brand'}`">
            <el-icon size="20">
              <component :is="stat.icon" />
            </el-icon>
          </span>
        </div>
        <strong>{{ stat.value }}</strong>
        <div class="stat-card__bottom">
          <span :class="`is-${stat.tone ?? 'brand'}`">{{ stat.sub }}</span>
        </div>
      </article>
    </section>

    <section class="dashboard-grid">
      <article class="panel panel--wide">
        <div class="panel__head">
          <h2>近 7 日营业趋势</h2>
        </div>
        <div ref="trendChartEl" class="chart" />
      </article>

      <article class="panel">
        <div class="panel__head">
          <h2>订单状态分布</h2>
        </div>
        <div ref="pieChartEl" class="chart" />
      </article>
    </section>

    <section class="dashboard-grid dashboard-grid--bottom">
      <article class="panel">
        <div class="panel__head">
          <h2>门店业绩排行</h2>
        </div>
        <div class="rank-list">
          <div v-for="(store, index) in storeRankItems" :key="store.storeId" class="rank-item">
            <span class="rank-index">{{ index + 1 }}</span>
            <div>
              <strong>{{ store.storeName }}</strong>
              <small>{{ store.orderCount }} 单 · 客单价 {{ formatAmount(store.avgPrice) }}</small>
            </div>
            <div class="rank-item__right">
              <strong>{{ formatAmount(store.revenue) }}</strong>
              <small>营收</small>
            </div>
          </div>
          <el-empty v-if="!storeRankItems.length" description="暂无门店排行数据" />
        </div>
      </article>

      <article class="panel">
        <div class="panel__head">
          <h2>热销商品 Top 3</h2>
        </div>
        <div class="product-list">
          <div v-for="product in hotProducts" :key="product.productId" class="product-item">
            <div class="product-item__cover">{{ getProductInitial(product.productName) }}</div>
            <div>
              <strong>{{ product.productName }}</strong>
              <small>{{ product.sales }} 杯 · {{ formatAmount(product.salesAmount) }}</small>
            </div>
            <el-icon><ArrowRight /></el-icon>
          </div>
          <el-empty v-if="!hotProducts.length" description="暂无热销商品数据" />
        </div>
      </article>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import * as echarts from 'echarts'
import { ElMessage } from 'element-plus'
import {
  ArrowRight,
  Clock,
  CreditCard,
  Download,
  ShoppingBag,
  TrendCharts,
  User,
  Warning,
} from '@element-plus/icons-vue'
import { OrderStatus, ORDER_STATUS_LABELS } from '@jiaofa/constants'
import { formatMoney } from '@jiaofa/shared-utils'
import {
  fetchDashboardOverview,
  type DashboardDataPoint,
  type DashboardHotProductItem,
  type DashboardOverview,
  type DashboardStoreRankItem,
} from '@/api/dashboard'
import { exportDashboardReport } from './utils/dashboardExport'
import { useUserStore } from '@/stores/user'

const trendChartEl = ref<HTMLDivElement>()
const pieChartEl = ref<HTMLDivElement>()
const overview = ref<DashboardOverview>()
const loading = ref(false)
const exporting = ref(false)
const userStore = useUserStore()
const dashboardTitle = computed(() => userStore.isPlatformScope ? '总部运营工作台' : '门店运营工作台')
let trendChart: echarts.ECharts | undefined
let pieChart: echarts.ECharts | undefined

const storeRankItems = computed<DashboardStoreRankItem[]>(() => overview.value?.storeRank ?? [])
const hotProducts = computed<DashboardHotProductItem[]>(() => overview.value?.hotProducts ?? [])
const trendPoints = computed<DashboardDataPoint[]>(() => overview.value?.trend ?? [])
const updatedAtText = computed(() =>
  loading.value
    ? '数据加载中...'
    : `数据更新于: ${new Date().toLocaleString('zh-CN', { hour12: false })}`,
)
const statCards = computed(() => [
  {
    label: '今日营业额',
    value: formatAmount(overview.value?.todayRevenue ?? 0),
    sub: '实时汇总',
    tone: 'success' as const,
    icon: TrendCharts,
  },
  ...(userStore.isPlatformScope && overview.value?.todayRechargeAmount != null
    ? [{
        label: '今日充值金额',
        value: formatAmount(overview.value.todayRechargeAmount),
        sub: '平台实时汇总',
        tone: 'brand' as const,
        icon: CreditCard,
      }]
    : []),
  {
    label: '今日订单数',
    value: String(overview.value?.todayOrderCount ?? 0),
    sub: '今日新增',
    tone: 'success' as const,
    icon: ShoppingBag,
  },
  {
    label: '本月营业额',
    value: formatAmount(overview.value?.monthRevenue ?? 0),
    sub: '累计实收',
    tone: 'brand' as const,
    icon: CreditCard,
  },
  {
    label: '待处理订单',
    value: String(overview.value?.pendingOrderCount ?? 0),
    sub: (overview.value?.pendingOrderCount ?? 0) > 0 ? '需要关注' : '状态正常',
    tone:
      (overview.value?.pendingOrderCount ?? 0) > 0 ? ('warning' as const) : ('success' as const),
    icon: Warning,
  },
  {
    label: '待处理退款',
    value: String(overview.value?.pendingRefundCount ?? 0),
    sub: (overview.value?.pendingRefundCount ?? 0) > 0 ? '尽快审核' : '暂无积压',
    tone: (overview.value?.pendingRefundCount ?? 0) > 0 ? ('error' as const) : ('success' as const),
    icon: Clock,
  },
  {
    label: '总用户数',
    value: String(overview.value?.totalUsers ?? 0),
    sub: `营业门店 ${overview.value?.totalStores ?? 0} 家`,
    tone: 'brand' as const,
    icon: User,
  },
])

const resizeCharts = () => {
  trendChart?.resize()
  pieChart?.resize()
}

const renderTrendChart = async () => {
  await nextTick()
  if (trendChartEl.value) {
    trendChart ??= echarts.init(trendChartEl.value)
    trendChart.setOption({
      color: ['#76b7e8'],
      grid: { top: 12, right: 20, bottom: 28, left: 44 },
      tooltip: { trigger: 'axis' },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: trendPoints.value.map((item) => item.label),
        axisLine: { show: false },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: '#eef0f2' } },
      },
      series: [
        {
          type: 'line',
          smooth: true,
          data: trendPoints.value.map((item) => item.value / 100),
          areaStyle: { color: 'rgba(118, 183, 232, .14)' },
          symbolSize: 8,
          lineStyle: { width: 3 },
        },
      ],
    })
  }
}

const renderPieChart = async () => {
  await nextTick()
  if (pieChartEl.value) {
    pieChart ??= echarts.init(pieChartEl.value)
    const pieData = (overview.value?.statusDistribution ?? []).map((item) => ({
      name: ORDER_STATUS_LABELS[item.label as OrderStatus] ?? item.label,
      value: item.value,
    }))
    pieChart.setOption({
      color: ['#c8cdd2', '#f2a950', '#76b7e8', '#45b97c', '#e76565', '#8777d9'],
      tooltip: { trigger: 'item' },
      legend: { bottom: 0, itemWidth: 8, itemHeight: 8, textStyle: { color: '#73777b' } },
      series: [
        {
          type: 'pie',
          radius: ['48%', '68%'],
          center: ['50%', '42%'],
          label: { show: false },
          data: pieData,
        },
      ],
    })
  }
}

const loadOverview = async () => {
  loading.value = true
  try {
    overview.value = await fetchDashboardOverview()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '经营概览加载失败')
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await loadOverview()
  await renderTrendChart()
  await renderPieChart()
  window.addEventListener('resize', resizeCharts)
})

watch(
  () => overview.value,
  async () => {
    await renderTrendChart()
    await renderPieChart()
  },
)

onBeforeUnmount(() => {
  window.removeEventListener('resize', resizeCharts)
  trendChart?.dispose()
  pieChart?.dispose()
})

function formatAmount(amountInFen: number): string {
  return `¥${formatMoney(amountInFen)}`
}

function getProductInitial(name: string): string {
  return name.slice(0, 1).toUpperCase()
}

/**
 * 导出报表。
 *
 * 导出的指标值直接取 `statCards`（页面上渲染的同一个 computed），
 * 而不是从 `overview` 再算一遍——否则一旦页面口径调整，导出的数
 * 就会和用户看到的对不上，这种「两个真相」最难查。
 */
function handleExport(): void {
  if (!overview.value) {
    ElMessage.warning('数据尚未加载完成，请稍后再试')
    return
  }
  exporting.value = true
  try {
    const fileName = exportDashboardReport(overview.value, {
      generatedAt: new Date(),
      stats: statCards.value.map((stat) => ({ label: stat.label, value: stat.value })),
    })
    ElMessage.success(`已导出 ${fileName}`)
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '报表导出失败')
  } finally {
    exporting.value = false
  }
}
</script>

<style scoped lang="scss">
.operation-dashboard {
  max-width: 1440px;
  margin: 0 auto;
}
.operation-dashboard__heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.operation-dashboard__heading h1 {
  margin: 0;
  color: var(--jf-color-text-primary);
  font-size: 20px;
  font-weight: 800;
}
.operation-dashboard__heading span {
  color: var(--jf-color-text-secondary);
  font-size: 14px;
}
.operation-dashboard__heading-right {
  display: flex;
  align-items: center;
  gap: 16px;
}
.export-button {
  height: 38px;
  padding: 0 16px;
  border: 0;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--jf-color-primary);
  color: #fff;
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s;
}
.export-button:hover:not(:disabled) {
  background: var(--jf-color-primary-dark);
}
.export-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.stat-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 16px;
}
.stat-card {
  min-height: 138px;
  padding: 16px;
  border: 1px solid var(--jf-color-border);
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 2px 10px rgba(37, 40, 43, 0.04);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.stat-card__top,
.stat-card__bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.stat-card__top span:first-child {
  color: var(--jf-color-text-secondary);
  font-size: 14px;
}
.stat-card strong {
  color: var(--jf-color-text-primary);
  font-size: 26px;
  line-height: 1;
}
.stat-card__bottom span {
  font-size: 12px;
  font-weight: 700;
}
.stat-card__icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  background: rgba(179, 218, 247, 0.3);
  color: var(--jf-color-primary-dark);
}
.is-brand {
  color: var(--jf-color-primary-dark);
}
.is-success {
  color: var(--jf-color-success);
}
.is-warning {
  color: var(--jf-color-warning);
}
.is-error {
  color: var(--jf-color-error);
}
.dashboard-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 16px;
  margin-top: 16px;
}
.dashboard-grid--bottom {
  grid-template-columns: 1fr 1fr;
}
.panel {
  min-height: 320px;
  padding: 20px;
  border: 1px solid var(--jf-color-border);
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 2px 10px rgba(37, 40, 43, 0.04);
}
.panel__head {
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.panel__head h2 {
  margin: 0;
  color: var(--jf-color-text-primary);
  font-size: 16px;
  font-weight: 800;
}
.chart {
  width: 100%;
  height: 240px;
}
.rank-list,
.product-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.rank-item,
.product-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  border-radius: 10px;
  transition: background 0.2s;
}
.rank-item:hover,
.product-item:hover {
  background: rgba(252, 250, 246, 0.9);
}
.rank-index {
  width: 26px;
  height: 26px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: rgba(118, 183, 232, 0.16);
  color: var(--jf-color-primary-dark);
  font-size: 13px;
  font-weight: 800;
}
.rank-item div:nth-child(2),
.product-item div {
  min-width: 0;
  flex: 1;
}
.rank-item strong,
.product-item strong {
  display: block;
  color: var(--jf-color-text-primary);
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rank-item small,
.product-item small {
  display: block;
  margin-top: 4px;
  color: var(--jf-color-text-secondary);
  font-size: 12px;
}
.rank-item__right {
  text-align: right;
}
.product-item__cover {
  width: 46px;
  height: 46px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  background: rgba(118, 183, 232, 0.16);
  color: var(--jf-color-primary-dark);
  font-size: 18px;
  font-weight: 800;
}
.product-item .el-icon {
  color: var(--jf-color-text-secondary);
}
@media (max-width: 1280px) {
  .stat-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .dashboard-grid,
  .dashboard-grid--bottom {
    grid-template-columns: 1fr;
  }
}
</style>
